import React from 'react';
import {
  Box,
  Container,
  VStack,
  Heading,
  Text,
  Button,
  Icon,
  useColorModeValue,
  HStack,
  UnorderedList,
  ListItem,
} from '@chakra-ui/react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, CalendarBlank } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { PageMeta } from '../components/PageMeta';

// Articles data from history.md
const articles = {
  'narodziny-kostki-rubika': {
    id: 1,
    title: 'Narodziny Kostki Rubika (1974–1980)',
    year: 1974,
    lead: 'W 1974 roku węgierski profesor i architekt, Ernő Rubik, stworzył łamigłówkę, która miała zrewolucjonizować świat gier logicznych. Pozornie prosty sześcian z kolorowymi ściankami szybko podbił serca zarówno entuzjastów matematyki, jak i zwykłych amatorów zagadek na całym świecie. W tym odcinku przyjrzymy się początkom kostki Rubika oraz temu, jak przemierzyła drogę od prototypu w pracowni profesora do masowej produkcji w latach 80.',
    bulletPoints: [
      '1974 rok – Ernő Rubik konstruuje pierwszą wersję mechanicznej łamigłówki w Budapeszcie.',
      '1975 rok – Rubik uzyskuje patent na swoją "kostkę magiczną".',
      'Współpraca z Ideal Toy – kluczowy krok w wejściu kostki na rynki międzynarodowe.',
      'Renaming – z "Magic Cube" na "Rubik\'s Cube", czyli znana dziś "Kostka Rubika".',
      '1980 rok – kostka oficjalnie trafia na rynek globalny i szybko staje się bestsellerem.',
    ],
    content: [
      {
        subtitle: 'Początki wynalazku',
        text: 'Wszystko zaczęło się od zwykłej fascynacji przestrzenią i mechaniką. Ernő Rubik – wówczas wykładowca na Wydziale Wzornictwa Wnętrz i Architektury Wnętrz w Budapeszcie – postanowił stworzyć model, który pomagałby studentom lepiej rozumieć zasady trójwymiarowej struktury. Tak narodziła się idea kostki z obrotowymi elementami.\n\nPrototyp wykonany był z drewna i gumek recepturek, które pozwalały na niezależne obracanie segmentów sześcianu. Choć na początku wcale nie było oczywiste, że tę łamigłówkę da się ułożyć z powrotem do stanu wyjściowego, Rubikowi udało się znaleźć rozwiązanie po kilku tygodniach prób i błędów.',
      },
      {
        subtitle: 'Patent i pierwsze sukcesy',
        text: 'W 1975 roku Rubik złożył wniosek o patent, zabezpieczając unikatowy mechanizm obrotowych klocków. Następne lata przyniosły mu szersze zainteresowanie branży zabawek. Współpraca z amerykańskim koncernem Ideal Toy okazała się kluczowa – to dzięki niej "Magic Cube" przemianowana została na "Rubik\'s Cube" i ruszyła w świat.',
      },
      {
        subtitle: 'Globalny debiut',
        text: 'Oficjalny światowy debiut przypadł na rok 1980. W krótkim czasie kostka Rubika stała się niebywałym hitem – z półek sklepowych znikały miliony egzemplarzy. Zainteresowanie kostką było tak duże, że szybko zaczęto organizować konkursy na czas ułożenia oraz tworzyć kluby pasjonatów. Rozpoczął się prawdziwy szał na "układanie kolorowych ścianek", a kostka Rubika na dobre weszła do popkultury, stając się ikoną lat 80.',
      },
    ],
  },
  'fenomen-kostki': {
    id: 2,
    title: 'Fenomen Kostki w latach 80. i pierwsze Mistrzostwa Świata',
    year: 1980,
    lead: 'Lata 80. to czas, w którym kostka Rubika opanowała salony, szkoły i miejsca spotkań młodzieży na całym świecie. Publikowano poradniki, tworzono kluby hobbystyczne, a w mediach non stop dyskutowano o coraz lepszych rekordach szybkości. Ten odcinek przybliża rozkwit fenomenu kostki w dekadzie, która przyniosła jej największą popularność.',
    bulletPoints: [
      'Ogromna sprzedaż – już w pierwszym roku globalnej dystrybucji sprzedano kilkadziesiąt milionów egzemplarzy.',
      'Książki i poradniki – powstawały masowo, by pomóc fanom w rozgryzieniu tajników łamigłówki.',
      '1982 rok – pierwsze Mistrzostwa Świata w układaniu kostki Rubika (Budapeszt).',
      'Pierwsi rekordziści – wyłonienie najszybszych zawodników, którzy układali kostkę w kilkadziesiąt sekund.',
      'Kultura masowa – obecność w filmach, serialach, reklamach i teledyskach.',
    ],
    content: [
      {
        subtitle: 'Narastający szał',
        text: 'Po sukcesie handlowym w 1980 roku kostka Rubika stała się prawdziwą sensacją. Sprzedawana w dziesiątkach milionów egzemplarzy, była nie tylko zabawką, ale i symbolem inteligencji, sprytu czy cierpliwości. Coraz więcej osób podejmowało wyzwanie, by układać ją w jak najkrótszym czasie, co zachęcało do wymiany metod i patentów. Tak narodziła się pierwsza społeczność speedcuberów.',
      },
      {
        subtitle: 'Pierwsze rekordy i poradniki',
        text: 'W księgarniach zaczęły pojawiać się publikacje, tłumaczące – krok po kroku – jak przejść od losowej konfiguracji do uporządkowanych, jednokolorowych ścianek. Książki te często wyprzedawały się na pniu, a autorzy stawali się sławni w kręgu entuzjastów. W tamtym okresie rekordy wynosiły kilkadziesiąt sekund – i wydawało się to nie lada wyczynem!',
      },
      {
        subtitle: 'Pierwsze Mistrzostwa Świata',
        text: 'W 1982 roku w Budapeszcie odbyły się pierwsze oficjalne Mistrzostwa Świata w układaniu kostki Rubika. Najlepsi zawodnicy układali wtedy kostkę w czasie poniżej 30 sekund. Na podium znalazła się światowa czołówka, a całe wydarzenie wzbudzało ogromne zainteresowanie mediów, pokazując, że Rubik\'s Cube z prostego gadżetu stała się pełnoprawną dyscypliną sportową.',
      },
      {
        subtitle: 'Wpływ na kulturę',
        text: 'Motyw kostki Rubika regularnie pojawiał się w programach telewizyjnych i filmach. Zabawka ta kojarzona była z "mądrymi umysłami" i postrzegana jako wyzwanie dla każdego, kto chce się sprawdzić w sprytnym myśleniu. Popularność ugruntowały m.in. występy kostki w reklamach oraz w teledyskach. Nie było wątpliwości – moda na kostkę Rubika na dobre zadomowiła się w popkulturze.',
      },
    ],
  },
  'matematyka-kostki': {
    id: 3,
    title: 'Matematyka Kostki Rubika – Liczba Permutacji, Google i "God\'s Number"',
    year: 2010,
    lead: 'Ile tak naprawdę istnieje możliwych ułożeń kostki Rubika? Czy istnieje ruch idealny, który z każdej konfiguracji sprowadzi nas do rozwiązania w minimalnej liczbie kroków? W latach 80. i 90. rozgorzała żywa dyskusja matematyczna wokół tych pytań, a w 2010 roku do ustalenia prawdy przyczyniła się… moc obliczeniowa Google. Poznaj tajniki matematycznej strony kostki.',
    bulletPoints: [
      '43 tryliony – szacowana liczba permutacji kostki (ok. 43,252,003,274,489,856,000).',
      '"God\'s Number" – minimalna liczba ruchów wystarczająca do ułożenia kostki z dowolnej pozycji.',
      'Rok 2010 – współpraca naukowców z Google, która dowiodła, że "God\'s Number" = 20 (w standardowej metryce ruchów).',
      'Zastosowania matematyczne – teoria grup, łamanie szyfrów, sztuczna inteligencja.',
      'Komputery vs. ludzie – szybkość rozwiązywania i poszukiwanie algorytmów optymalnych.',
    ],
    content: [
      {
        subtitle: 'Liczba wszystkich ułożeń',
        text: 'Od początku badań nad kostką Rubika matematycy próbowali oszacować, ile istnieje odmiennych konfiguracji. Dziś już wiemy, że ta liczba wynosi około 43 tryliony (43,252,003,274,489,856,000) – wartość wręcz niewyobrażalna! Mimo tak astronomicznej liczby permutacji, kostkę można zawsze ułożyć – wystarczy znać odpowiednie sekwencje ruchów.',
      },
      {
        subtitle: 'Zagadnienie "God\'s Number"',
        text: 'Wraz z analizą permutacji pojawiło się pytanie: ile minimalnie ruchów potrzeba, by rozwiązać kostkę z absolutnie każdej możliwej konfiguracji? W środowisku matematycznym tę teoretyczną wartość nazwano "liczbą Boga" (ang. God\'s Number). Przez lata kolejne badania obniżały górną granicę szacunków. Początkowo sądzono, że może to być 22, 21… aż w końcu doprecyzowano, że minimalną liczbą w standardowej metryce ruchów (każdy obrót ściany liczy się za jeden ruch) jest 20.',
      },
      {
        subtitle: 'Rok 2010 i moc obliczeniowa Google',
        text: 'Przełom w ustaleniu ostatecznych dowodów na to, że God\'s Number wynosi 20, nastąpił właśnie w 2010 roku, kiedy to naukowcy współpracujący z Google wykorzystali ogromną moc obliczeniową, by "przesiać" i sprawdzić, czy istnieje jakakolwiek konfiguracja kostki wymagająca więcej niż 20 ruchów. Po starannych analizach potwierdzono, że nie – 20 ruchów to absolutne maksimum wymagane do ułożenia.',
      },
      {
        subtitle: 'Znaczenie w matematyce',
        text: 'Badając kostkę, matematycy zagłębiają się w teorię grup, która pozwala lepiej zrozumieć własności symetrii i przekształceń w układach. Skutkuje to nie tylko większą wiedzą o samej kostce Rubika, ale znajduje też zastosowania w kryptografii i algorytmice. Pojawienie się specjalistycznych programów do rozwiązywania kostki Rubika stało się impulsem do rozwoju sztucznej inteligencji, która potrafi wnioskować o optymalnych drogach do celu.',
      },
      {
        subtitle: 'Człowiek kontra maszyna',
        text: 'Mimo że algorytmy komputerowe potrafią ułożyć kostkę w kilka sekund (a czasem i znacznie szybciej, korzystając z predefiniowanych rozwiązań), wciąż fascynują nas ludzie pokonujący barierę kilkunastu czy nawet kilkunastu dziesiątych sekund w zawodach speedcubingowych. Jednak to właśnie matematyka i komputery pozwoliły ostatecznie poznać granicę możliwości – i potwierdzić, że nikt nie jest w stanie przekroczyć magicznej liczby 20 ruchów.',
      },
    ],
  },
  'speedcubing': {
    id: 4,
    title: 'Speedcubing i Biały Krzyż – Współczesne Metody Układania',
    year: 2020,
    lead: 'Współcześnie kostka Rubika nie jest tylko pamiątką z lat 80. – to pełnoprawny sport z międzynarodowymi zawodami, sponsorami i plejadą rekordzistów. Jednym z najpopularniejszych podejść do szybkiego układania (speedcubingu) jest metoda CFOP (Fridrich), której pierwszym etapem często bywa ułożenie tzw. białego krzyża. Sprawdź, jak dziś miłośnicy kostki podchodzą do bicia rekordów i dlaczego biała ścianka jest tak ważna w procesie układania.',
    bulletPoints: [
      'Speedcubing – sportowe układanie kostki na czas, liczone w sekundach, a nawet ułamkach sekundy.',
      'Metody układania – m.in. CFOP, Roux, ZZ – każda kładzie nacisk na inne etapy i algorytmy.',
      'Biały krzyż – typowy pierwszy krok metody CFOP; zwykle da się go ułożyć w 6–8 ruchach (lub mniej) przy dobrej znajomości algorytmów.',
      'Poprawa czasów – dzięki ulepszonym technikom, odpowiednim smarom do kostek i innowacjom sprzętowym.',
      'Organizacje i mistrzostwa – World Cube Association nadzoruje zawody na całym świecie.',
    ],
    content: [
      {
        subtitle: 'Speedcubing jako sport',
        text: 'Wraz z rozwojem konkurencji w układaniu kostki Rubika, pojawił się termin speedcubing, czyli sportowe podejście do rozwiązywania łamigłówki na czas. Zawodnicy starają się maksymalnie zoptymalizować ruchy, redukując je do efektywnych sekwencji. Pojawienie się dedykowanych kostek o wyższej jakości (lżejsze, z lepszym mechanizmem) i specjalnych smarów sprawiło, że możliwe stało się uzyskiwanie wyników na poziomie 5–6 sekund.',
      },
      {
        subtitle: 'Metody układania',
        text: 'Najpopularniejszą i jedną z najbardziej wszechstronnych metod jest CFOP (nazywana też metodą Fridrich, od nazwiska Jessiki Fridrich, która ją spopularyzowała). Skrót pochodzi od etapów:\n\nCross (krzyż)\nF2L (first two layers)\nOLL (orienting last layer)\nPLL (permuting last layer)\n\nOprócz niej popularne są też metody Roux czy ZZ, jednak to właśnie CFOP dominuje wśród profesjonalnych speedcuberów.',
      },
      {
        subtitle: 'Biały krzyż – pierwszy kluczowy etap',
        text: 'Większość graczy rozpoczyna od ułożenia białego krzyża na dole lub na górze. Celem jest prawidłowe ustawienie czterech klocków z białymi naklejkami tak, by tworzyły krzyż dopasowany do kolorów środkowych klocków sąsiednich ścianek. Przy odpowiednim wytrenowaniu i opanowaniu tzw. "finger tricków" (szybkich obrotów palcami) białą ściankę można ustawić już w zaledwie kilku ruchach – zwykle w granicach 6–8, choć zaawansowani gracze potrafią zejść jeszcze niżej, jeśli poświęcą więcej czasu na analizę początkowej konfiguracji.',
      },
      {
        subtitle: 'Rekordy i organizacje',
        text: 'Współcześnie istnieje World Cube Association (WCA), międzynarodowa organizacja nadzorująca zawody i ustanawiająca oficjalne zasady. Zawodnicy startują nie tylko w układaniu kostki Rubika 3×3, ale także w różnych wariantach (2×2, 4×4, 5×5 i większe), a nawet w konkurencjach z zasłoniętymi oczami czy jedną ręką. Rekordy świata są regularnie poprawiane; najlepsi speedcuberzy przełamują kolejne granice, skracając czas ułożenia do niecałych 4 sekund.',
      },
      {
        subtitle: 'Nowa era układania',
        text: 'Obecnie kostka Rubika to coś znacznie więcej niż tylko przebrzmiała ciekawostka z lat 80. Zyskała status sportu i przedmiotu badań naukowych, a jej popularyzacji sprzyja internet, w którym można znaleźć tysiące tutoriali, algorytmów i poradników. Rynek oferuje też wiele rodzajów kostek – od klasycznych modeli, przez lżejsze, magnetyczne, po elektroniczne wersje z sensorami. Wszystko po to, by dążyć do jeszcze większej precyzji i szybkości.',
      },
    ],
  },
  'znak-towarowy': {
    id: 5,
    title: 'Znak Towarowy "Rubik\'s" a Inne Marki na Rynku Speedcubingu',
    year: 2016,
    lead: 'Od lat 80. kostka Rubika zaskarbiła sobie miejsce w sercach milionów ludzi. Szybko zyskała również wartość biznesową, co w naturalny sposób pociągało za sobą kwestie związane z ochroną znaków towarowych i patentów. W tym odcinku przyglądamy się, jak znak towarowy "Rubik\'s" ewoluował na przestrzeni lat, w jaki sposób był broniony przed naruszeniami i jak to wszystko wpłynęło na rozkwit innych marek w świecie speedcubingu.',
    bulletPoints: [
      'Ochrona marki "Rubik\'s" – znak towarowy i prawa do nazwy oraz charakterystycznego wyglądu łamigłówki.',
      'Kwestie patentowe i wzór przemysłowy – wygasanie patentów i późniejsza ochrona designu kostki.',
      'Spory prawne – m.in. procesy o zastrzeżenie kształtu kostki w UE oraz dyskusje o tym, co jest funkcjonalnością, a co wzorem.',
      'Rynek speedcubingowy – pojawienie się alternatywnych marek, takich jak Gan, MoYu, QiYi, DaYan czy Yuxin.',
      'Wpływ na rozwój kostek – innowacje technologiczne i mechaniczne, które poszły dalej niż oryginalna kostka Rubika.',
    ],
    content: [
      {
        subtitle: 'Narodziny i ochrona pierwotnego patentu',
        text: 'Węgierski wynalazca Ernő Rubik opatentował swoje dzieło (ówcześnie pod nazwą "Magic Cube") w połowie lat 70. XX wieku. Po nawiązaniu współpracy z Ideal Toy kostka zyskała światową sławę już jako "Rubik\'s Cube". Początkowo w grę wchodziły dwa rodzaje ochrony:\n\nPatent na mechanizm – dotyczył samego sposobu łączenia i obracania elementów łamigłówki.\nZnak towarowy – chroniący nazwę "Rubik\'s" oraz (w niektórych regionach świata) charakterystyczny wygląd kostki.\n\nPatenty mechaniczne mają jednak swoją datę wygaśnięcia. Wraz z upływem czasu, gdy wygasła ochrona patentowa na mechanizm obrotu, rynek zalały produkty różnych firm, oparte na tej samej zasadzie działania.',
      },
      {
        subtitle: 'Znak towarowy i wzór przemysłowy',
        text: 'Mimo że patent na "obracające się klocki" stracił ważność, Rubik\'s Brand Ltd. starała się chronić wizerunek i nazwę. W wielu krajach zarejestrowano znak towarowy "Rubik\'s", a także różne wariacje graficzne (np. logo). Ponadto firma usiłowała zabezpieczyć kostkę jako wzór przemysłowy – zwłaszcza w odniesieniu do charakterystycznego, sześciennego kształtu z widocznymi podziałami na 9 kwadratów na każdej ściance.',
      },
      {
        subtitle: 'Spory prawne i ich konsekwencje',
        text: 'Najgłośniejszym przykładem walki o ochronę wzoru przemysłowego była sprawa toczona przed Trybunałem Sprawiedliwości Unii Europejskiej. Firma Rubik\'s starała się zastrzec "trójwymiarowy kształt kostki" jako znak towarowy (3D trademark). Po latach batalii, w 2016 roku orzeczono, że funkcjonalne cechy obrotowego mechanizmu i sześcienny kształt nie mogą być chronione samym znakiem towarowym. Oznaczało to, że producenci alternatywnych kostek (o zbliżonej konstrukcji) mogli działać swobodniej na rynku UE, byle nie naruszali nazwy "Rubik\'s" i nie kopiowali elementów charakterystycznego logo czy oznaczeń.',
      },
      {
        subtitle: 'Pojawienie się nowych marek',
        text: 'Kiedy originalna ochrona patentowa straciła ważność, a dyskusje o zastrzeżeniu kształtu kostki nie przyniosły "pełnej" wygranej Rubik\'s Brand Ltd., rynek zaczął rosnąć w zawrotnym tempie. Powstało wiele niezależnych firm produkujących kostki do speedcubingu:\n\nGan – słynie z lekkich, magnetycznych konstrukcji premium, stosowanych przez wielu rekordzistów.\nMoYu – pionier licznych innowacji mechanicznych w kostkach, oferuje szeroką gamę modeli.\nQiYi (znane również jako MoFangGe) – produkty cenione za dobry stosunek jakości do ceny.\nDaYan, Yuxin, MGC i wiele innych – również przyciągają pasjonatów różnorodnymi rozwiązaniami (np. magnetyczne rdzenie, lepsze systemy antypoślizgowe, różne typy sprężyn itp.).',
      },
      {
        subtitle: 'Konsekwencje dla speedcuberów',
        text: 'Dzisiejsza różnorodność marek nie tylko nie osłabiła fenomenu kostki Rubika, ale wręcz go wzmocniła. Speedcuberzy mają do wyboru szereg zaawansowanych technologicznie produktów. Usprawnienia wprowadzone przez niezależne firmy (np. magnesy, nowe typy śrubek i sprężyn, wyjątkowo gładkie powierzchnie) pozwoliły zawodnikom osiągać coraz szybsze czasy.',
      },
    ],
  },
  'rewolucja-w-mechanice': {
    id: 6,
    title: 'Rewolucja w Mechanice Kostek – Ballcore, Maglev i Inne Technologie',
    year: 2022,
    lead: 'Szybkie układanie kostki Rubika to dziś nie tylko kwestia wyćwiczonych algorytmów i zręcznych palców. Równie ważny jest sam sprzęt – producenci kostek wprowadzają coraz bardziej zaawansowane rozwiązania techniczne, by uczynić kostki szybszymi, stabilniejszymi i wygodniejszymi w obsłudze. W tym odcinku przyjrzymy się najnowszym technologiom, które zrewolucjonizowały rynek speedcubingu, takim jak ballcore czy maglev.',
    bulletPoints: [
      'Ballcore – kulisty rdzeń, zapewniający lepsze wycentrowanie i płynniejsze obracanie.',
      'Maglev – system zastępujący sprężyny parą odpychających się magnesów, zmniejszający tarcie i poprawiający stabilność.',
      'Technologie magnetyczne – od prostych magnesów w krawędziach i narożnikach, po regulowane systemy magnetyczne.',
      'Poprawiona regulacja napięcia – wielostopniowe systemy regulacji elastyczności, m.in. za pomocą specjalnych nakrętek i sprężyn.',
      'Wpływ na speedcubing – czasy ułożenia są coraz krótsze, a błędy rotacyjne ograniczone do minimum.',
    ],
    content: [
      {
        subtitle: 'Ballcore – stabilność i płynność w jednym',
        text: 'Klasyczna kostka Rubika opiera się na sześciennym mechanizmie, w którym sześć środkowych elementów pełni funkcję osi. Wraz z rozwojem technologii producenci zaczęli eksperymentować z kulistymi (lub półkulistymi) rdzeniami – znanymi jako ballcore – w których środki i wewnętrzne elementy ślizgają się po gładkiej, sferycznej powierzchni.\n\nLepsze wycentrowanie: Kształt kuli sprawia, że poszczególne elementy kostki są równo rozłożone wokół rdzenia, co ogranicza efekt "chwiania" się ścianek.\nPłynniejsze ruchy: Zastosowanie wysokiej jakości tworzyw sztucznych i gładkich powierzchni zmniejsza tarcie między elementami, dzięki czemu można wykonywać obroty z mniejszym wysiłkiem i szybciej.',
      },
      {
        subtitle: 'Maglev – innowacja zamiast sprężyn',
        text: 'Przez lata podstawową metodą kontroli napięcia w kostkach były śruby i sprężyny. Sprężyny jednak ulegają ścieraniu, generują tarcie i mogą powodować efekt drgania. W odpowiedzi pojawił się maglev (skrót od "magnetic levitation"), w którym:\n\nDwie magnesy są ustawione biegunami tak, by się odpychały.\nZastępują one sprężynę, zapewniając podobny lub wyższy opór, ale przy minimalnym tarciu.\nW efekcie kostka jest bardziej sprężysta, a jednocześnie cichsza i płynniejsza przy szybkich obrotach.',
      },
      {
        subtitle: 'Technologie magnetyczne wewnątrz elementów',
        text: 'W nowoczesnych kostkach nie brakuje również magnesów wbudowanych w krawędzie (edge) i narożniki (corner). Najczęściej projektuje się je tak, by:\n\nStabilizować ścianki w momencie wyrównania kostki, co minimalizuje błędy w trakcie szybkich ruchów.\nPoprawiać tzw. corner cutting, czyli zdolność kostki do płynnego obrotu nawet wtedy, gdy ściany nie są idealnie wyrównane.\nRegulować siłę magnetyczną – za pomocą przełączników czy wymiennych kapsułek, co pozwala dopasować kostkę do indywidualnych preferencji gracza.',
      },
      {
        subtitle: 'Zaawansowane systemy regulacji',
        text: 'Poza ballcore i maglev na rynku znajdziemy także:\n\nNakrętki o kilku poziomach regulacji (ang. dual adjustment system) – gdzie jednocześnie można ustawić moc ścisku (tension) i kompresję sprężyny (lub siłę odpychania magnesów w systemach maglev).\nSmarowanie i łożyskowanie – specjalne smary i zestawy łożysk mogą dodatkowo zmniejszyć tarcie wewnątrz kostki.',
      },
      {
        subtitle: 'Wpływ innowacji na speedcubing',
        text: 'Dzięki technologiom takim jak ballcore, maglev i usprawnionym magnetycznym systemom, kostki są coraz szybsze i bardziej wybaczające błędy. To otwiera zawodnikom drogę do osiągania lepszych czasów ułożenia, sięgających już granic 3–4 sekund w oficjalnych próbach. W efekcie rozgrywki speedcubingowe stają się bardziej widowiskowe, a różnice w czołówce często zależą od drobnych niuansów – zarówno w umiejętnościach zawodnika, jak i w szczegółach konstrukcyjnych wybranego modelu kostki.',
      },
    ],
  },
};

const HistoryArticle = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const timelineColor = useColorModeValue('brand.500', 'brand.200');

  const article = articles[slug as keyof typeof articles];

  if (!article) {
    return (
      <Container maxW="container.xl" py={8}>
        <VStack spacing={8} align="stretch">
          <Text>{t('history.articleNotFound')}</Text>
          <Button
            leftIcon={<ArrowLeft />}
            onClick={() => navigate('/history')}
            variant="ghost"
            width="fit-content"
          >
            {t('common.backToHistory')}
          </Button>
        </VStack>
      </Container>
    );
  }

  return (
    <>
      <PageMeta
        title={article.title}
        description={article.lead}
      />
      
      <Container maxW="container.xl" py={8}>
        <VStack spacing={8} align="stretch">
          <Button
            leftIcon={<ArrowLeft />}
            onClick={() => navigate('/history')}
            variant="ghost"
            width="fit-content"
          >
            {t('common.backToHistory')}
          </Button>

          <VStack spacing={4} align="start">
            <HStack color={timelineColor}>
              <Icon as={CalendarBlank} weight="bold" />
              <Text>{article.year}</Text>
            </HStack>
            <Heading size="2xl">{article.title}</Heading>
          </VStack>

          <Text fontSize="xl" fontStyle="italic">
            {article.lead}
          </Text>

          <Box>
            <Heading size="md" mb={4}>{t('history.keyPoints')}</Heading>
            <UnorderedList spacing={2}>
              {article.bulletPoints.map((point, index) => (
                <ListItem key={index}>{point}</ListItem>
              ))}
            </UnorderedList>
          </Box>

          <VStack spacing={8} align="stretch">
            {article.content.map((section, index) => (
              <Box key={index}>
                <Heading size="lg" mb={4}>{section.subtitle}</Heading>
                {section.text.split('\n\n').map((paragraph, pIndex) => (
                  <Text key={pIndex} mb={4}>
                    {paragraph}
                  </Text>
                ))}
              </Box>
            ))}
          </VStack>
        </VStack>
      </Container>
    </>
  );
};

export default HistoryArticle; 